import type {
  ChordModifier,
  FocusPolicy,
  KeybindingCommandId,
  Platform,
} from '../model/keybindingTypes';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import {
  isKeybindingEditorVisible,
  scopedTargetRoots,
  type ResolvedKeybindingTarget,
} from './targetRegistry';

export const KEYBINDING_CHROME_SELECTOR = '[data-keybinding-chrome]';
export const COMMAND_PALETTE_INPUT_SELECTOR = '[data-testid="command-palette-input"]';
export const AI_CHAT_SELECTOR = '[data-testid="ai-chat-sidebar"]';
export const XTERM_SELECTOR = '.xterm';
export const KEYBINDING_OWN_INPUT_SELECTOR = [
  '[data-keybinding-own-input]',
  '[data-testid="notebook-markdown-cell"]',
].join(',');

const TEXT_INPUT_SELECTOR = [
  'input',
  'textarea',
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
].join(',');

export type FocusDocument = {
  readonly activeElement: Element | null;
  readonly body?: Element | null;
};

export type ResolveFocusOptions = {
  readonly commandId: KeybindingCommandId;
  readonly policy: FocusPolicy;
  readonly worksInTerminal?: boolean;
  readonly resolvedModifiers?: readonly ChordModifier[];
  readonly targetPlatform?: Platform;
  readonly target: ResolvedKeybindingTarget | null;
  readonly documentObject?: FocusDocument;
};

function closest(element: Element | null, selector: string): Element | null {
  return element?.closest(selector) ?? null;
}

function isInside(root: Element | null, element: Element | null): boolean {
  return Boolean(root && element && (root === element || root.contains(element)));
}

function hasEditorFocus(
  target: ResolvedKeybindingTarget | null,
  commandId: KeybindingCommandId,
): boolean {
  return target?.getEditor(commandId)?.hasTextFocus() === true;
}

function isForeignTextInput(activeElement: Element): boolean {
  const textInput = closest(activeElement, TEXT_INPUT_SELECTOR);
  if (!textInput) return false;
  if (closest(activeElement, '.monaco-editor .find-widget')) return true;
  return !closest(activeElement, '.monaco-editor') &&
    !closest(activeElement, KEYBINDING_OWN_INPUT_SELECTOR);
}

function registeredScopedRoots(target: ResolvedKeybindingTarget): readonly Element[] {
  const tabIds = new Set(useWorkspaceStore.getState().tabs.map((tab) => tab.id));
  if (target.tabId) tabIds.add(target.tabId);
  return scopedTargetRoots(tabIds);
}

function isInsideDifferentScopedRoot(
  activeElement: Element,
  activeRoot: Element | null,
  scopedRoots: readonly Element[],
): boolean {
  return scopedRoots.some((candidateRoot) =>
    candidateRoot !== activeRoot &&
    !isInside(activeRoot, candidateRoot) &&
    !isInside(candidateRoot, activeRoot) &&
    isInside(candidateRoot, activeElement)
  );
}

export function resolveFocus(options: ResolveFocusOptions): boolean {
  const documentObject = options.documentObject ?? globalThis.document;
  const activeElement = documentObject?.activeElement ?? null;
  const insideTerminal = Boolean(closest(activeElement, XTERM_SELECTOR));
  if (insideTerminal) {
    const macCommandChord = options.targetPlatform === 'mac' &&
      options.resolvedModifiers?.includes('Cmd') === true;
    if (options.worksInTerminal !== true && !macCommandChord) return false;
    if (options.policy === 'anywhere') return true;
    if (options.policy === 'tab') return options.target !== null;
    return isInside(options.target?.getRootElement(options.commandId) ?? null, activeElement);
  }

  if (options.policy === 'anywhere') return true;
  if (!options.target) return false;
  const nothingFocused = !activeElement || activeElement === documentObject.body;

  if (!nothingFocused) {
    if (closest(activeElement, COMMAND_PALETTE_INPUT_SELECTOR)) return false;
    if (closest(activeElement, AI_CHAT_SELECTOR)) return false;
  }

  const root = options.target.getRootElement(options.commandId);
  if (options.policy === 'sql-editor') {
    if (hasEditorFocus(options.target, options.commandId)) return true;
    if (nothingFocused) {
      return isKeybindingEditorVisible(options.target.getEditor(options.commandId));
    }
    const chrome = closest(activeElement, KEYBINDING_CHROME_SELECTOR);
    if (chrome && isInside(root, chrome)) return true;
    if (!isInside(root, activeElement)) return false;
    if (closest(activeElement, '.monaco-editor')) return false;
    return !isForeignTextInput(activeElement);
  }

  if (nothingFocused) return true;
  if (hasEditorFocus(options.target, options.commandId)) return true;
  if (isForeignTextInput(activeElement)) return false;
  return !isInsideDifferentScopedRoot(
    activeElement,
    root,
    registeredScopedRoots(options.target),
  );
}
